/**
 * useSaveFlow — the ONE shared "ALPACA-style save" pattern for every form
 * (CLAUDE.md §18.6 / §14a):
 *   1. show the big "Do you want to Save?" confirmation (Yes/No);
 *   2. on Yes, run the form's existing save (MediatR command/handler — never
 *      bypassed; the handler audits via IAuditTrailService and persists UTC);
 *   3. on success, show the "Saved successfully." acknowledgement;
 *   4. then return the user to that form's own list view (`onSaved`).
 *
 *   const runSave = useSaveFlow();
 *   await runSave({ save: () => mutateAsync(payload), onSaved: () => goToList() });
 */
import { useCallback } from 'react';
import { useConfirm } from './useConfirm';
import { useReasonGate } from './useReasonGate';
import { useNotify } from '../notifications';
import { runLoading } from '../loading';
import { runWithAuditReason } from '../../api/client';
import { FORM_MSG } from '../../formKit/messages';
import { serverMessage } from './serverMessage';
import { showDialog, type DialogRequest } from './dialogBus';
import { resetUnsavedTracking } from '../../hooks/useUnsavedGuard';

export interface SaveFlowOptions {
  /** Pre-save confirmation dialog title (default: "Save"). Override together
   *  with confirmMessage when the action reads as something other than a
   *  plain save (e.g. "Update" for an edit-in-place form). */
  confirmTitle?: string;
  /** Pre-save confirmation message (default: ALPACA "Do you want to Save?"). */
  confirmMessage?: string;
  /** The actual save — call the existing command/handler/mutation here. */
  save: () => Promise<void> | void;
  /** Success message (default: ALPACA "Saved successfully.").
   *
   *  Pass a FUNCTION when the message depends on what the save produced — a
   *  minted record ID, say. A plain string is fixed when the options object is
   *  built, which is BEFORE `save` runs, so `successMessage: minted ? ... : x`
   *  silently resolves against the pre-save value. */
  successMessage?: string | (() => string | undefined);
  /** How success is shown: big 'dialog' (default, ALPACA-style box) or 'toast'. */
  successAs?: 'dialog' | 'toast';
  /** Milestone-success dialog (BO UI Standard §13) — when a save mints a NEW
   *  record ID, return the rich success dialog (green, id + detail rows +
   *  next-step actions) here.  Evaluated AFTER the save so it can read the minted
   *  ID.  When set it REPLACES the default success message. */
  successDialog?: () => DialogRequest;
  /** Run after a successful save — e.g. navigate back to the form's list view.
   *  When `successDialog` was shown, `action` carries the VALUE of whichever
   *  button the user clicked (e.g. a milestone dialog's "Sample Login →"
   *  action) so the caller can branch on it — previously discarded, which
   *  left every action button behaving identically to the primary one. */
  onSaved?: (action?: string) => void;
  /** Custom failure handler (defaults to an error toast). */
  onError?: (e: unknown) => void;

  // ── Audit reason-for-change (standing rule 11) — the GLOBAL mechanism ──────
  /** Audit form name (matches dbo.AuditData.FormName).  When set together with
   *  `auditAction: 'edit'`, the save automatically prompts for a reason if the
   *  form has ReasonRequired ON (Settings → Audit Trail Configuration) and
   *  attaches it to the audited mutation — so a form gets reason capture for FREE
   *  by passing these two props, with no per-form gate code. */
  formName?: string;
  /** 'edit' gates the reason; 'create' (or unset) is reason-exempt (§14a C3). */
  auditAction?: 'create' | 'edit';
}

export function useSaveFlow() {
  const { confirm, alert } = useConfirm();
  const notify = useNotify();
  const gateReason = useReasonGate();

  return useCallback(
    async (opts: SaveFlowOptions): Promise<boolean> => {
      // Reason-for-change gate (standing rule 11) — only on an EDIT, and only when
      // the form requires it; returns '' (no prompt) otherwise.  null = the user
      // cancelled the reason prompt → abort the whole save.
      let reason = '';
      if (opts.formName && opts.auditAction === 'edit') {
        const r = await gateReason(opts.formName, 'edit');
        if (r === null) return false;
        reason = r;
      }
      const ok = await confirm({ title: opts.confirmTitle ?? 'Save', message: opts.confirmMessage ?? FORM_MSG.SAVE_CONFIRM });
      if (!ok) return false;
      try {
        // Blocking global loader (§18.6): shows the scrim + configured symbol
        // for the save's lifetime and prevents a double-submit.  runWithAuditReason
        // arms the X-Audit-Reason header so the API persists the reason on the
        // audit row with no per-handler wiring.
        await runLoading(runWithAuditReason(reason, async () => { await opts.save(); }), {
          blocking: true,
          label: 'Saving…',
        });
        // Persisted → the form is clean again, so leaving it must not warn
        // "Unsaved changes will be lost" (hooks/useUnsavedGuard.ts).
        resetUnsavedTracking();
        // Milestone-success dialog (§13) wins when provided (ID + detail rows +
        // next-step actions); else the standard dialog/toast acknowledgement.
        let action: string | undefined;
        if (opts.successDialog) {
          const result = await showDialog(opts.successDialog());
          if (typeof result === 'string') action = result;
        } else {
          // Resolved HERE, after the save, so a function can read what it minted.
          const msg = (typeof opts.successMessage === 'function'
            ? opts.successMessage() : opts.successMessage) ?? FORM_MSG.SAVED;
          if ((opts.successAs ?? 'dialog') === 'dialog') {
            await alert({ title: 'Success', message: msg, tone: 'success' });
          } else {
            notify.success(msg);
          }
        }
        opts.onSaved?.(action);
        return true;
      } catch (e) {
        if (opts.onError) opts.onError(e);
        // The API's own reason when it sent one (a rule refusal says WHY),
        // exactly as useDeleteFlow does; the generic text only for a bare failure.
        else notify.error(serverMessage(e) ?? FORM_MSG.SAVE_FAILED);
        return false;
      }
    },
    [confirm, alert, notify, gateReason],
  );
}
